import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { ChatUiContext, ChatUiPayload } from '../client/chatClient';
import { RobbieActionKind, RobbieActivity, createRobbieActivity } from '../utils/robbieActivity';
import { RobbieAppliedBus, RobbieAppliedEvent, RobbieAppliedKind, createRobbieAppliedBus } from '../utils/robbieAppliedBus';

/**
 * Bridge between what the buyer is looking at, and doing, and Robbie's chat.
 *
 * - The view: step components report their visible selection (pointers only —
 *   ids and names, never copies of server data), and useChat snapshots it at
 *   send time so the backend can ground answers in the current view ("this
 *   component" means the one on screen).
 * - The activity: steps record what the buyer just did ("Kept 10 of 13
 *   lines"); the last few ride along with every message, so "what did I just
 *   change?" is answered from fact. One buffer per RFQ, see
 *   `utils/robbieActivity`.
 *
 * Ref-based on purpose: reporting a selection change or an action must not
 * re-render the wizard tree, and both are only ever read at the moment a
 * message is sent.
 *
 * - The ask: the conversation's send registers itself here, so a surface far
 *   from the wizard's props (a modal inside a sub-step) can hand Robbie a
 *   message on the buyer's behalf.
 *
 * - The applied: a Robbie card the buyer applied tells the step on screen what
 *   changed, so its working copy updates at once (see `utils/robbieAppliedBus`).
 *
 * The provider must sit ABOVE everything that records or sends — including
 * the wizard page's own `useWizardRobbie`, which holds the conversation.
 */
type RobbieSend = (text: string) => void;

const SnapshotContext = createContext<React.MutableRefObject<ChatUiContext | null> | null>(null);
const ActivityContext = createContext<RobbieActivity | null>(null);
const SendContext = createContext<React.MutableRefObject<RobbieSend | null> | null>(null);
const AppliedContext = createContext<RobbieAppliedBus | null>(null);

export const RobbieUiProvider: React.FC<{ rfqId?: string; children: React.ReactNode }> = ({ rfqId, children }) => {
  const ref = useRef<ChatUiContext | null>(null);
  const send = useRef<RobbieSend | null>(null);
  // A new RFQ gets its own buffer, loaded from this tab's storage.
  const activity = useMemo(() => (rfqId ? createRobbieActivity(rfqId) : null), [rfqId]);
  // Per RFQ, so an event held for one RFQ's step never lands on another's.
  const applied = useMemo(() => createRobbieAppliedBus(), [rfqId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <SnapshotContext.Provider value={ref}>
      <ActivityContext.Provider value={activity}>
        <SendContext.Provider value={send}>
          <AppliedContext.Provider value={applied}>{children}</AppliedContext.Provider>
        </SendContext.Provider>
      </ActivityContext.Provider>
    </SnapshotContext.Provider>
  );
};

/**
 * Report the current view. Pass a plain object each render — updates are
 * deduped by value, and the snapshot clears when the reporter unmounts, so a
 * step can never leak its context into the next one.
 */
export const useReportRobbieUiContext = (ctx: ChatUiContext | null) => {
  const ref = useContext(SnapshotContext);
  const serialized = JSON.stringify(ctx);
  useEffect(() => {
    if (!ref) return;
    ref.current = serialized === 'null' ? null : JSON.parse(serialized);
    return () => {
      ref.current = null;
    };
  }, [ref, serialized]);
};

/**
 * Record something the buyer just did, in their words. Safe anywhere: outside
 * the provider (a page with no Robbie) it does nothing.
 */
export const useRecordRobbieAction = (): ((kind: RobbieActionKind, text: string) => void) => {
  const activity = useContext(ActivityContext);
  return useCallback((kind, text) => activity?.record(kind, text), [activity]);
};

/**
 * Getter for the send path: the view on screen at call time (or `override`,
 * for a send a step makes as its view closes) plus the buyer's recent actions.
 * Null when there is nothing to send.
 */
export const useRobbieUiSnapshot = (): ((override?: ChatUiContext) => ChatUiPayload | null) => {
  const ref = useContext(SnapshotContext);
  const activity = useContext(ActivityContext);
  return useCallback(
    (override) => {
      const view = override ?? (ref ? ref.current : null);
      const recentActions = activity ? activity.recent() : [];
      if (!view && recentActions.length === 0) return null;
      return { ...view, ...(recentActions.length > 0 ? { recentActions } : {}) };
    },
    [ref, activity]
  );
};

/** The conversation's owner registers its send; it clears on unmount. */
export const useRegisterRobbieSend = (send: RobbieSend) => {
  const ref = useContext(SendContext);
  useEffect(() => {
    if (!ref) return undefined;
    ref.current = send;
    return () => {
      ref.current = null;
    };
  }, [ref, send]);
};

/**
 * Send Robbie a message on the buyer's behalf from anywhere under the
 * provider. Null outside it (a gallery, a page with no Robbie), so a caller
 * can leave out a control that would do nothing.
 */
export const useAskRobbie = (): RobbieSend | null => {
  const ref = useContext(SendContext);
  const ask = useCallback((text: string) => ref?.current?.(text), [ref]);
  return ref ? ask : null;
};

/** Tell the step on screen that a Robbie card was applied. A no-op outside the provider. */
export const usePublishRobbieApplied = (): ((event: RobbieAppliedEvent) => void) => {
  const bus = useContext(AppliedContext);
  return useCallback((event) => bus?.publish(event), [bus]);
};

/**
 * A step's working copy hears the cards applied to it. The handler is read
 * through a ref, so callers can pass an inline function.
 */
export const useOnRobbieApplied = <K extends RobbieAppliedKind>(
  kind: K,
  handler: (event: Extract<RobbieAppliedEvent, { kind: K }>) => void
) => {
  const bus = useContext(AppliedContext);
  const latest = useRef(handler);
  latest.current = handler;
  useEffect(() => {
    if (!bus) return undefined;
    return bus.subscribe(kind, (event) => latest.current(event));
  }, [bus, kind]);
};
